import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Globe, Eye, Users, CalendarBlank, Package, Warning, FileText, FloppyDisk } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PortalConfigTab({ lang }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const lb = lang === 'es' ? {
    title: 'Configuracion del Portal de Clientes',
    desc: 'Controla que secciones son visibles para los clientes cuando acceden al portal de seguimiento de sus estudios. Esta configuracion es global y se aplica a todos los clientes.',
    welcome: 'Mensaje de bienvenida',
    welcomeHint: 'Texto que aparece en la parte superior del dashboard del portal',
    sections: 'Secciones visibles',
    showVolunteers: 'Voluntarios',
    showVolunteersDesc: 'Muestra contadores agregados (total, incluidos, completados, excluidos, pendientes)',
    showVisitProgress: 'Progreso de visitas',
    showVisitProgressDesc: 'Muestra barras de progreso por cada visita del estudio',
    showSamples: 'Muestras',
    showSamplesDesc: 'Muestra contadores de muestras, stock y dispensaciones',
    showAdverse: 'Eventos adversos',
    showAdverseDesc: 'Muestra tabla de eventos adversos con codigo de voluntario anonimizado',
    showDocuments: 'Documentos',
    showDocumentsDesc: 'Permitir acceso a documentos compartidos (funcionalidad futura)',
    save: 'Guardar configuracion',
    saved: 'Configuracion guardada',
    loading: 'Cargando...',
    portalUrl: 'URL del portal',
  } : {
    title: 'Client Portal Configuration',
    desc: 'Control which sections are visible to clients when they access the study tracking portal. This configuration is global and applies to all clients.',
    welcome: 'Welcome message',
    welcomeHint: 'Text shown at the top of the portal dashboard',
    sections: 'Visible sections',
    showVolunteers: 'Volunteers',
    showVolunteersDesc: 'Show aggregate counters (total, included, completed, excluded, pending)',
    showVisitProgress: 'Visit progress',
    showVisitProgressDesc: 'Show progress bars for each study visit',
    showSamples: 'Samples',
    showSamplesDesc: 'Show sample counts, stock and dispensations',
    showAdverse: 'Adverse events',
    showAdverseDesc: 'Show adverse events table with anonymized volunteer code',
    showDocuments: 'Documents',
    showDocumentsDesc: 'Allow access to shared documents (future feature)',
    save: 'Save configuration',
    saved: 'Configuration saved',
    loading: 'Loading...',
    portalUrl: 'Portal URL',
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/config`, { withCredentials: true })
      .then(r => setConfig(r.data))
      .catch(() => toast.error('Error'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/portal/config`, config, { withCredentials: true });
      setConfig(r.data);
      toast.success(lb.saved);
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-12"><p className="text-sm text-stone-400">{lb.loading}</p></div>;
  if (!config) return null;

  const toggles = [
    { key: 'show_volunteer_counts', label: lb.showVolunteers, desc: lb.showVolunteersDesc, icon: Users, color: 'text-[#1f3e80]' },
    { key: 'show_visit_progress', label: lb.showVisitProgress, desc: lb.showVisitProgressDesc, icon: CalendarBlank, color: 'text-[#1f3e80]' },
    { key: 'show_samples', label: lb.showSamples, desc: lb.showSamplesDesc, icon: Package, color: 'text-[#1f3e80]' },
    { key: 'show_adverse_effects', label: lb.showAdverse, desc: lb.showAdverseDesc, icon: Warning, color: 'text-amber-500' },
    { key: 'show_documents', label: lb.showDocuments, desc: lb.showDocumentsDesc, icon: FileText, color: 'text-stone-400' },
  ];

  return (
    <div className="space-y-6 max-w-2xl" data-testid="portal-config-tab">
      <div className="flex items-center gap-3">
        <Globe size={22} weight="duotone" className="text-[#1f3e80]" />
        <div>
          <h2 className="text-base font-heading font-semibold text-stone-900">{lb.title}</h2>
          <p className="text-xs text-stone-500 mt-0.5">{lb.desc}</p>
        </div>
      </div>

      {/* Portal URL */}
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800 flex items-center gap-2">
        <Eye size={14} className="flex-shrink-0" />
        <span><strong>{lb.portalUrl}:</strong>{' '}
          <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-[11px]">{window.location.origin}/portal/login</code>
        </span>
      </div>

      {/* Welcome message */}
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-stone-800">{lb.welcome}</Label>
        <Input
          data-testid="portal-welcome-input"
          value={config.welcome_message || ''}
          onChange={e => setConfig(p => ({ ...p, welcome_message: e.target.value }))}
          className="bg-white border-stone-200"
          placeholder={lb.welcomeHint}
        />
        <p className="text-[11px] text-stone-400">{lb.welcomeHint}</p>
      </div>

      {/* Section toggles */}
      <div className="space-y-1">
        <Label className="text-sm font-medium text-stone-800">{lb.sections}</Label>
        <div className="space-y-2 mt-2">
          {toggles.map(t => {
            const Icon = t.icon;
            const enabled = config[t.key] !== false;
            return (
              <div key={t.key} className={`flex items-center gap-3 p-3 rounded border transition-colors ${enabled ? 'bg-white border-stone-200' : 'bg-stone-50 border-stone-100'}`}>
                <button
                  type="button"
                  onClick={() => setConfig(p => ({ ...p, [t.key]: !enabled }))}
                  data-testid={`portal-toggle-${t.key}`}
                  className={`relative w-10 h-5.5 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-stone-300'}`}
                  style={{ width: 40, height: 22 }}
                >
                  <div className={`absolute top-0.5 left-0.5 w-[18px] h-[18px] bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-[18px]' : ''}`} />
                </button>
                <Icon size={16} className={`flex-shrink-0 ${enabled ? t.color : 'text-stone-300'}`} />
                <div className="flex-1 min-w-0">
                  <span className={`text-sm font-medium ${enabled ? 'text-stone-800' : 'text-stone-400'}`}>{t.label}</span>
                  <p className={`text-[11px] ${enabled ? 'text-stone-500' : 'text-stone-300'}`}>{t.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end pt-2">
        <Button
          data-testid="save-portal-config-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1.5"
        >
          <FloppyDisk size={14} />
          {saving ? '...' : lb.save}
        </Button>
      </div>
    </div>
  );
}
